import { useCallback, useSyncExternalStore } from 'react';
import dayjs, { ConfigType } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useUser } from '../context/UserContext';

dayjs.extend(isoWeek);

export const DateGroupList = {
  recentTransactions: 'recentTransactions',
  weeklyBudget: 'weeklyBudget',
  transactions: 'transactions',
  matterTransactions: 'matterTransactions',
  matterExpenses: 'matterExpenses',
  tasks: 'tasks',
  criticalTasks: 'criticalTasks',
  workTimes: 'workTimes',
  worktimeAgenda: 'worktimeAgenda',
  timerSlots: 'timerSlots',
} as const;

// The last collapse all / expand all, plus the groups the user flipped since then.
interface IListCollapseState {
  areAllCollapsed: boolean;
  flippedKeys: ReadonlySet<string>;
}

const ALL_EXPANDED: IListCollapseState = { areAllCollapsed: false, flippedKeys: new Set() };

// Held in memory only, so a reload or reopening the add-in starts every list expanded again.
const stateByList = new Map<string, IListCollapseState>();
const listeners = new Set<() => void>();

export const toDateGroupKey = (date: ConfigType) => dayjs(date).format('YYYY-MM-DD');

export const toWeekGroupKey = (date: ConfigType) => {
  const day = dayjs(date);
  return `${day.isoWeekYear()}-W${day.isoWeek()}`;
};

const toStoreKey = (userId: string, listKey: string) => `${userId}:${listKey}`;

// Module state outlives a single test, so tests start each case from a clean slate.
export const resetCollapsedDateGroups = () => stateByList.clear();

const isGroupCollapsed = (state: IListCollapseState, groupKey: string) =>
  state.areAllCollapsed !== state.flippedKeys.has(groupKey);

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const updateListState = (
  storeKey: string,
  update: (state: IListCollapseState) => IListCollapseState,
) => {
  const current = stateByList.get(storeKey) ?? ALL_EXPANDED;
  const next = update(current);
  if (next === current) return;
  stateByList.set(storeKey, next);
  listeners.forEach((listener) => listener());
};

const withGroupFlipped = (state: IListCollapseState, groupKey: string): IListCollapseState => {
  const flippedKeys = new Set(state.flippedKeys);
  if (!flippedKeys.delete(groupKey)) flippedKeys.add(groupKey);
  return { ...state, flippedKeys };
};

const withAllCollapsed = (areAllCollapsed: boolean): IListCollapseState => ({
  areAllCollapsed,
  flippedKeys: new Set(),
});

export const useCollapsedDateGroups = (listKey: string) => {
  const { user } = useUser();
  const userId = user?.id;
  const storeKey = userId ? toStoreKey(userId, listKey) : undefined;
  const state = useSyncExternalStore(
    subscribe,
    () => (storeKey && stateByList.get(storeKey)) || ALL_EXPANDED,
  );

  const updateState = useCallback(
    (update: (current: IListCollapseState) => IListCollapseState) => {
      if (!storeKey) return;
      updateListState(storeKey, update);
    },
    [storeKey],
  );

  const isCollapsed = useCallback((groupKey: string) => isGroupCollapsed(state, groupKey), [state]);

  const toggle = useCallback(
    (groupKey: string) => updateState((current) => withGroupFlipped(current, groupKey)),
    [updateState],
  );

  const expand = useCallback(
    (groupKey: string) =>
      updateState((current) =>
        isGroupCollapsed(current, groupKey) ? withGroupFlipped(current, groupKey) : current,
      ),
    [updateState],
  );

  // Collapses the whole list while every given group is open; otherwise expands the whole list.
  const getToggleAllControl = useCallback(
    (groupKeys: string[]) => {
      if (groupKeys.length === 0) return undefined;
      const areAllExpanded = !groupKeys.some(isCollapsed);
      return {
        areAllExpanded,
        onToggleAll: () => updateState(() => withAllCollapsed(areAllExpanded)),
      };
    },
    [isCollapsed, updateState],
  );

  return { isCollapsed, toggle, expand, getToggleAllControl };
};
